import { useEffect, useState, type FormEvent } from 'react';
import type { VideoItem, VideoThumbnailOption } from '../types';
import { formatBytes } from '../utils';
import { validVideoDimensions, videoFrameStyle } from '../videoLayout';
import { Icon } from './Icon';
import { Modal } from './Modal';

const maxThumbnailBytes = 10 * 1024 * 1024;
const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);

type ThumbnailMode = 'video' | 'upload';

function errorCode(reason: unknown): string {
  if (!reason || typeof reason !== 'object' || !('details' in reason)) return '';
  const details = reason.details;
  if (!details || typeof details !== 'object') return '';
  if ('code' in details && typeof details.code === 'string') return details.code;
  if (!('error' in details) || !details.error || typeof details.error !== 'object') return '';
  return 'code' in details.error && typeof details.error.code === 'string' ? details.error.code : '';
}

export type ThumbnailSelection =
  | { type: 'frame'; timeSeconds: number }
  | { type: 'upload'; file: File };

interface ThumbnailDialogProps {
  video: VideoItem | null;
  busy: boolean;
  error: string;
  onClose: () => void;
  loadOptions: (videoId: string) => Promise<VideoThumbnailOption[]>;
  onSubmit: (selection: ThumbnailSelection) => void;
}

export function ThumbnailDialog({ video, busy, error, onClose, loadOptions, onSubmit }: ThumbnailDialogProps) {
  const [mode, setMode] = useState<ThumbnailMode>('video');
  const [options, setOptions] = useState<VideoThumbnailOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [frameSourceUnavailable, setFrameSourceUnavailable] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [localError, setLocalError] = useState('');
  const [frameDimensions, setFrameDimensions] = useState<{ videoId: string; width: number; height: number } | null>(null);
  const [previewDimensions, setPreviewDimensions] = useState<{ url: string; width: number; height: number } | null>(null);
  const [failedPreview, setFailedPreview] = useState('');

  useEffect(() => {
    setMode('video');
    setOptions([]);
    setOptionsError('');
    setFrameSourceUnavailable(false);
    setSelectedTime(null);
    setFile(null);
    setLocalError('');
    setLoadAttempt(0);
    setFrameDimensions(null);
    setPreviewDimensions(null);
    setFailedPreview('');
  }, [video?.id]);

  const videoId = video?.id;

  useEffect(() => {
    if (!videoId) return;
    let active = true;
    setOptionsLoading(true);
    setOptionsError('');
    setFrameSourceUnavailable(false);
    void loadOptions(videoId).then((loaded) => {
      if (!active) return;
      setOptions(loaded);
      if (loaded.length === 0) setOptionsError('No video frames were available. You can upload an image instead.');
    }).catch((reason: unknown) => {
      if (!active) return;
      if (errorCode(reason) === 'video_media_unavailable') {
        setFrameSourceUnavailable(true);
        setMode('upload');
        return;
      }
      setOptionsError(reason instanceof Error ? reason.message : 'Could not generate thumbnail choices.');
    }).finally(() => {
      if (active) setOptionsLoading(false);
    });
    return () => { active = false; };
  }, [loadAttempt, loadOptions, videoId]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function chooseFile(selected: File | undefined): void {
    setLocalError('');
    if (!selected) {
      setFile(null);
      return;
    }
    if (!allowedTypes.has(selected.type)) {
      setLocalError('Choose a JPEG, PNG, or WebP image.');
      setFile(null);
      return;
    }
    if (selected.size > maxThumbnailBytes) {
      setLocalError('Thumbnail images must be 10 MB or smaller.');
      setFile(null);
      return;
    }
    setFile(selected);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (busy) return;
    if (mode === 'video' && selectedTime !== null) onSubmit({ type: 'frame', timeSeconds: selectedTime });
    if (mode === 'upload' && file) onSubmit({ type: 'upload', file });
  }

  const selectedOption = options.find((option) => option.timeSeconds === selectedTime);
  const displayedPreview = mode === 'upload' ? previewUrl || video?.thumbnailUrl : selectedOption?.dataUrl || video?.thumbnailUrl;
  const canSubmit = mode === 'video' ? selectedTime !== null : Boolean(file);
  const dimensions = video && validVideoDimensions(video) ? video
    : frameDimensions && frameDimensions.videoId === videoId ? frameDimensions
    : previewDimensions && previewDimensions.url === displayedPreview ? previewDimensions : {};
  const frameStyle = videoFrameStyle(dimensions, 28);
  const portrait = validVideoDimensions(dimensions) && dimensions.height > dimensions.width;

  return <Modal
    open={Boolean(video)}
    title="Change thumbnail"
    description={video ? `Choose a frame from “${video.title}” or upload a custom poster image.` : ''}
    onClose={onClose}
    width="medium"
    footer={<>
      <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="submit" form="thumbnail-dialog-form" className="button button--primary" disabled={!canSubmit || busy}>{busy ? 'Saving…' : 'Save thumbnail'}</button>
    </>}
  >
    <form id="thumbnail-dialog-form" className="dialog-form thumbnail-dialog" onSubmit={submit}>
      {(error || localError) && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error || localError}</p>}
      <div className="thumbnail-dialog__modes" role="tablist" aria-label="Thumbnail source">
        <button type="button" role="tab" aria-selected={mode === 'video'} className={mode === 'video' ? 'is-active' : ''} onClick={() => setMode('video')} disabled={busy || frameSourceUnavailable} title={frameSourceUnavailable ? 'The source video was removed after processing.' : undefined}>Choose from video</button>
        <button type="button" role="tab" aria-selected={mode === 'upload'} className={mode === 'upload' ? 'is-active' : ''} onClick={() => setMode('upload')} disabled={busy}>Upload image</button>
      </div>

      <div className="thumbnail-dialog__preview" style={frameStyle}>
        {displayedPreview && failedPreview !== displayedPreview
          ? <img src={displayedPreview} alt="Thumbnail preview"
              onLoad={(event) => setPreviewDimensions({ url: displayedPreview, width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
              onError={() => setFailedPreview(displayedPreview)} />
          : <span><Icon name="film" size={30} />{displayedPreview ? 'Thumbnail preview unavailable' : 'No current thumbnail'}</span>}
      </div>

      {mode === 'video' ? <section className="thumbnail-dialog__video-panel" aria-label="Choose a frame from the video">
        {optionsLoading ? <div className="thumbnail-dialog__loading"><span className="spinner" />Generating five choices from the video…</div> : optionsError ? <div className="thumbnail-dialog__options-error">
          <p role="alert">{optionsError}</p>
          <button type="button" className="button button--secondary" onClick={() => setLoadAttempt((attempt) => attempt + 1)} disabled={busy}>Try again</button>
        </div> : <div className={`thumbnail-dialog__options${portrait ? ' thumbnail-dialog__options--portrait' : ''}`} role="radiogroup" aria-label="Video thumbnail options">
          {options.map((option, index) => <button
            type="button"
            role="radio"
            aria-checked={selectedTime === option.timeSeconds}
            aria-label={`${option.positionPercent}% through video`}
            tabIndex={selectedTime === option.timeSeconds || (selectedTime === null && index === 0) ? 0 : -1}
            className={selectedTime === option.timeSeconds ? 'is-selected' : ''}
            style={{ aspectRatio: frameStyle.aspectRatio }}
            key={option.positionPercent}
            onClick={() => setSelectedTime(option.timeSeconds)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
                : (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length;
              setSelectedTime(options[next]!.timeSeconds);
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
            }}
            disabled={busy}
          >
            <img src={option.dataUrl} alt="" onLoad={(event) => setFrameDimensions({ videoId: videoId!, width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} />
            <span>{option.positionPercent}%</span>
          </button>)}
        </div>}
      </section> : <div className="thumbnail-dialog__upload-panel">
        {frameSourceUnavailable && <p className="thumbnail-dialog__source-note" role="status"><Icon name="film" size={16} />The source video was removed after processing, so new frame choices are unavailable. Upload a custom poster image instead.</p>}
        <label className="thumbnail-dialog__picker">
          Image file
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => chooseFile(event.target.files?.[0])} disabled={busy} />
          <small>{file ? `${file.name} · ${formatBytes(file.size)}` : 'JPEG, PNG, or WebP · maximum 10 MB'}</small>
        </label>
      </div>}
    </form>
  </Modal>;
}
